import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Play, Calendar, Activity } from 'lucide-react';
import { WorkflowManagementTab } from './WorkflowManagementTab';
import { SchedulingTab, ExecutionHistoryTab } from './scheduling';

/**
 * WorkflowManagementWrapper
 *
 * A wrapper component that provides tabs for:
 * 1. Manual Trigger - The existing WorkflowManagementTab for on-demand execution
 * 2. Automatic Scheduling - The scheduling feature for automated workflows
 * 3. Execution History - Unified view of all executions with status
 *
 * This keeps the existing WorkflowManagementTab unchanged while adding
 * the new scheduling and execution history functionality.
 */
export const WorkflowManagementWrapper: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'manual' | 'scheduling' | 'history'>('manual');

  const tabTriggerClass =
    'h-full rounded-none border-b-2 border-transparent ' +
    'data-[state=active]:border-primary data-[state=active]:bg-transparent px-1 pb-3';

  return (
    <div className='space-y-6'>
      {/* Main Tab Navigation */}
      <Tabs
        value={activeTab}
        onValueChange={v => setActiveTab(v as 'manual' | 'scheduling' | 'history')}
      >
        <TabsList className='bg-transparent h-12 p-0 space-x-6 border-b w-full justify-start'>
          <TabsTrigger value='manual' className={tabTriggerClass}>
            <Play className='h-4 w-4 mr-2' />
            Manual Trigger
          </TabsTrigger>
          <TabsTrigger value='scheduling' className={tabTriggerClass}>
            <Calendar className='h-4 w-4 mr-2' />
            Automatic Scheduling
          </TabsTrigger>
          <TabsTrigger value='history' className={tabTriggerClass}>
            <Activity className='h-4 w-4 mr-2' />
            Execution History
          </TabsTrigger>
        </TabsList>

        <TabsContent value='manual' className='mt-6'>
          <WorkflowManagementTab />
        </TabsContent>

        <TabsContent value='scheduling' className='mt-6'>
          <SchedulingTab />
        </TabsContent>

        <TabsContent value='history' className='mt-6'>
          <ExecutionHistoryTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WorkflowManagementWrapper;
